"use client"

import type { ReactNode } from 'react'
import { MapPin } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ExperienceEvidenceCard } from '@/components/profile/experience-evidence-card'
import { TopSkillChip } from '@/components/profile/top-skill-chip'
import { PersonContactSection } from '@/components/ui/entity-contact-sections'
import { extraSocialContactFrom } from '@/lib/social-networks'
import { MarkdownBody } from '@/components/ui/markdown-content'
import { candidateSections, type Certification, type Education, type ProfileData, type ProfilePaper } from '@/hooks/use-profile'
import { formatProfileCertificationDates, formatProfileEvidenceDateRange, formatProfilePaperMeta } from '@/lib/profile-evidence-display'
import { formatEntityLocation } from '@/utils/format-entity-location'
import { getPersonDisplayMeta } from '@/lib/person-identity-display'
import { sortDemonstratedSkillSummaries, splitClaimedOnlySkills } from '@/utils/profile-skills-display'
import { getTopSkillLinkedEvidence } from '@/utils/top-skill-evidence-links'

export type PersonProfileReadViewProps = {
  profile: ProfileData
  maxSkills?: number
  maxExperiences?: number
  maxEducation?: number
  maxPapers?: number
  maxCertifications?: number
  /** When true, show the full standing document (no list caps; education and certifications when present). */
  standingDocument?: boolean
  /** Optional badges / metadata row beside the identity block (match %, stage, etc.). */
  trailing?: ReactNode
  skillsHeading?: string
  /** Muted helper copy under the skills heading (employer framing vs Analysis). */
  skillsHelper?: string
  /** Section order for body blocks after identity. Default preserves Summary → Skills → Experience. */
  sectionOrder?: 'default' | 'experienceFirst'
  /** When true, wrap Summary / Skills / Experience each in a Card (Analysis-like rhythm). */
  sectionCards?: boolean
  /** Content between identity and summary (applied date, prospect headline, etc.). */
  afterIdentity?: ReactNode
  footer?: ReactNode
}

export function personDisplayName(profile: ProfileData): string {
  return getPersonDisplayMeta(profile).formalName || 'Candidate'
}

function joinProfileLine(parts: Array<string | null | undefined>): string {
  return parts.filter((part) => part != null && part.trim() !== '').join(' · ')
}

function paperRowKey(item: ProfilePaper, index: number): string {
  return item.id ?? `${item.title}-${index}`
}

function educationRowKey(item: Education, index: number): string {
  return item.id ?? `${item.institution}-${item.degree}-${index}`
}

function certificationRowKey(item: Certification, index: number): string {
  return item.id ?? `${item.name}-${item.issuer}-${index}`
}

function ProfileSection({
  title,
  sectionCards,
  children,
}: {
  title: string
  sectionCards?: boolean
  children: ReactNode
}) {
  if (sectionCards) {
    return (
      <Card>
        <CardHeader className="p-0 pb-3">
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">{children}</CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      {children}
    </div>
  )
}

function ProfileStandingRow({
  primary,
  secondary,
}: {
  primary: string
  secondary?: string
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
      <p className="text-sm font-medium">{primary}</p>
      {secondary ? <p className="mt-0.5 text-xs text-muted-foreground">{secondary}</p> : null}
    </div>
  )
}

export function PersonProfileReadView({
  profile,
  maxSkills = 12,
  maxExperiences = 3,
  maxEducation,
  maxPapers,
  maxCertifications,
  standingDocument = false,
  trailing,
  skillsHeading = 'Skills',
  skillsHelper,
  sectionOrder = 'default',
  sectionCards = false,
  afterIdentity,
  footer,
}: PersonProfileReadViewProps) {
  const candidate = candidateSections(profile)
  const location = formatEntityLocation(profile.city, profile.country)
  const allSkills = candidate.skills ?? []
  const allSkillSummaries = candidate.skill_summaries ?? []
  const hasSkillSummaries = allSkillSummaries.length > 0
  const demonstratedSummaries = hasSkillSummaries
    ? sortDemonstratedSkillSummaries(allSkillSummaries)
    : []
  const claimedOnlySkills = hasSkillSummaries
    ? splitClaimedOnlySkills(allSkills, allSkillSummaries)
    : []
  const visibleSummaries = standingDocument
    ? demonstratedSummaries
    : demonstratedSummaries.slice(0, maxSkills)
  const summariesTruncated =
    !standingDocument && demonstratedSummaries.length > visibleSummaries.length

  const allExperiences = candidate.experiences ?? []
  const allPapers = candidate.papers ?? []
  const allEducation = candidate.education ?? []
  const allCertifications = candidate.certifications ?? []

  const skills = standingDocument ? allSkills : allSkills.slice(0, maxSkills)
  const visibleExperiences = standingDocument
    ? allExperiences
    : allExperiences.slice(0, maxExperiences)
  const visiblePapers = standingDocument
    ? allPapers
    : maxPapers !== undefined
      ? allPapers.slice(0, maxPapers)
      : []
  const visibleEducation = standingDocument
    ? allEducation
    : maxEducation !== undefined
      ? allEducation.slice(0, maxEducation)
      : []
  const visibleCertifications = standingDocument
    ? allCertifications
    : maxCertifications !== undefined
      ? allCertifications.slice(0, maxCertifications)
      : []

  const skillsTruncated = !standingDocument && allSkills.length > skills.length

  const skillsTitle = hasSkillSummaries
    ? visibleSummaries.length > 0 || claimedOnlySkills.length > 0
      ? `${skillsHeading}${summariesTruncated ? ` (${demonstratedSummaries.length})` : ''}`
      : ''
    : skills.length > 0
      ? `${skillsHeading}${skillsTruncated ? ` (${allSkills.length})` : ''}`
      : ''

  const summarySection =
    profile.bio != null && profile.bio !== '' ? (
      <ProfileSection title="Summary" sectionCards={sectionCards}>
        <MarkdownBody plainClassName="whitespace-pre-wrap text-sm text-muted-foreground">
          {profile.bio}
        </MarkdownBody>
      </ProfileSection>
    ) : null

  const skillsSection = hasSkillSummaries ? (
    visibleSummaries.length > 0 || claimedOnlySkills.length > 0 ? (
      <ProfileSection title={skillsTitle} sectionCards={sectionCards}>
        {skillsHelper ? (
          <p className="mb-2 text-xs text-muted-foreground">{skillsHelper}</p>
        ) : null}
        {visibleSummaries.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {visibleSummaries.map((summary) => (
              <TopSkillChip
                key={summary.skill_id}
                summary={summary}
                linkedEvidence={getTopSkillLinkedEvidence(
                  summary.skill_name,
                  candidate.experiences,
                  candidate.education,
                )}
                displayOnly
              />
            ))}
          </div>
        ) : null}
        {claimedOnlySkills.length > 0 ? (
          <div className={visibleSummaries.length > 0 ? 'mt-3' : undefined}>
            <p className="mb-1.5 text-xs text-muted-foreground">Also listed</p>
            <div className="flex flex-wrap gap-1.5">
              {claimedOnlySkills.map((skill) => (
                <Badge key={skill} variant="secondarySoft" className="text-xs font-normal">
                  {skill}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
      </ProfileSection>
    ) : null
  ) : skills.length > 0 ? (
    <ProfileSection title={skillsTitle} sectionCards={sectionCards}>
      {skillsHelper ? (
        <p className="mb-2 text-xs text-muted-foreground">{skillsHelper}</p>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {skills.map((skill) => (
          <Badge key={skill} variant="secondarySoft" className="text-xs font-normal">
            {skill}
          </Badge>
        ))}
      </div>
    </ProfileSection>
  ) : null

  const experienceSection =
    visibleExperiences.length > 0 ? (
      <ProfileSection
        title={standingDocument ? 'Experience' : 'Recent experience'}
        sectionCards={sectionCards}
      >
        <div className="space-y-3">
          {visibleExperiences.map((exp) => (
            <div
              key={exp.id}
              className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
            >
              <ExperienceEvidenceCard experience={exp} variant="full" />
            </div>
          ))}
        </div>
      </ProfileSection>
    ) : null

  const papersSection =
    visiblePapers.length > 0 ? (
      <ProfileSection title="Publications" sectionCards={sectionCards}>
        <div className="space-y-3">
          {visiblePapers.map((item, index) => (
            <ProfileStandingRow
              key={paperRowKey(item, index)}
              primary={item.title}
              secondary={formatProfilePaperMeta(item)}
            />
          ))}
        </div>
      </ProfileSection>
    ) : null

  const educationSection =
    visibleEducation.length > 0 ? (
      <ProfileSection title="Education" sectionCards={sectionCards}>
        <div className="space-y-3">
          {visibleEducation.map((item, index) => {
            const dateRange = formatProfileEvidenceDateRange(
              item.start_date || undefined,
              item.end_date,
            )
            const secondary = [item.field_of_study, item.start_date ? dateRange : null]
              .filter(Boolean)
              .join(' · ')

            return (
              <ProfileStandingRow
                key={educationRowKey(item, index)}
                primary={joinProfileLine([item.degree, item.institution])}
                secondary={secondary || undefined}
              />
            )
          })}
        </div>
      </ProfileSection>
    ) : null

  const certificationsSection =
    visibleCertifications.length > 0 ? (
      <ProfileSection title="Certifications" sectionCards={sectionCards}>
        <div className="space-y-3">
          {visibleCertifications.map((item, index) => {
            const dateLine = formatProfileCertificationDates(item.issued_at, item.expires_at)
            const secondary = [dateLine, item.credential_id ? `ID ${item.credential_id}` : null]
              .filter(Boolean)
              .join(' · ')

            return (
              <ProfileStandingRow
                key={certificationRowKey(item, index)}
                primary={joinProfileLine([item.name, item.issuer])}
                secondary={secondary || undefined}
              />
            )
          })}
        </div>
      </ProfileSection>
    ) : null

  const bodySections =
    sectionOrder === 'experienceFirst'
      ? [
          experienceSection,
          papersSection,
          educationSection,
          certificationsSection,
          skillsSection,
          summarySection,
        ]
      : [summarySection, skillsSection, experienceSection, papersSection, educationSection, certificationsSection]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          {profile.headline ? (
            <p className="text-sm text-muted-foreground">{profile.headline}</p>
          ) : null}
          {location ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="size-3.5 shrink-0" aria-hidden />
              <span>{location}</span>
            </div>
          ) : null}
          <PersonContactSection
            email={profile.email}
            phone={profile.phone}
            website={profile.website}
            linkedin_url={profile.linkedin_url}
            x_url={profile.x_url}
            size="sm"
            {...extraSocialContactFrom(profile)}
          />
        </div>
        {trailing ? <div className="flex flex-wrap items-center gap-2">{trailing}</div> : null}
      </div>

      {afterIdentity}

      {bodySections}

      {footer ? <div className="flex flex-wrap gap-2 border-t border-border/60 pt-4">{footer}</div> : null}
    </div>
  )
}
